/**
 * AdminOrders — admin-only view of merchandise orders pulled from MongoDB.
 *
 * Lists orders newest-first with status filter, summary stats, and a
 * details drawer that shows customer info, shipping address, and line items.
 * Backed by GET /api/admin/orders (auth-protected).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Package,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  RefreshCw,
  X,
  Hash,
  Truck,
  Receipt,
  ShoppingBag,
  Trash2,
  Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "expired", label: "Expired" },
];

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusBadgeClass = (status) => {
  switch (status) {
    case "paid":
      return "bg-demolition-red text-white";
    case "pending":
      return "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40";
    case "expired":
      return "bg-white/10 text-white/50 border border-white/15";
    case "failed":
      return "bg-red-900/40 text-red-300 border border-red-500/40";
    default:
      return "bg-white/10 text-white/60 border border-white/15";
  }
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchOrders = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const result = await api.getOrders({ status: statusFilter || undefined, limit: 200 });
      setOrders(Array.isArray(result?.orders) ? result.orders : []);
    } catch (err) {
      logger.warn("Orders fetch failed:", err.message);
      toast.error("Failed to load orders");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getOrders({ status: statusFilter || undefined, limit: 200 });
        if (cancelled) return;
        setOrders(Array.isArray(result?.orders) ? result.orders : []);
      } catch (err) {
        if (cancelled) return;
        logger.warn("Orders fetch failed:", err.message);
        toast.error("Failed to load orders");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const handleRefresh = () => fetchOrders();

  const handleCleanupStale = async () => {
    setCleaning(true);
    try {
      const result = await api.refreshStaleOrders();
      const { checked = 0, paid = 0, expired = 0, still_pending: stillPending = 0 } = result || {};
      if (checked === 0) {
        toast.success("No stale pending orders to clean up.");
      } else {
        toast.success(
          `Checked ${checked} stale order${checked === 1 ? "" : "s"}: ${paid} paid, ${expired} expired, ${stillPending} still pending.`
        );
      }
      // refresh the list so newly-flipped statuses show up right away
      await fetchOrders({ silent: true });
    } catch (err) {
      logger.warn("Stale-order cleanup failed:", err.message);
      toast.error("Failed to clean up stale orders");
    } finally {
      setCleaning(false);
    }
  };

  const handleDelete = async (order) => {
    try {
      await api.deleteOrder(order.id);
      toast.success("Order deleted.");
      setSelected(null);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (err) {
      logger.warn("Delete order failed:", err.message);
      toast.error("Failed to delete order");
    }
  };

  const stats = useMemo(() => {
    const paid = orders.filter((o) => o.status === "paid");
    return {
      total: orders.length,
      paidCount: paid.length,
      paidRevenue: paid.reduce((s, o) => s + Number(o.amount_total || 0), 0),
      pendingCount: orders.filter((o) => o.status === "pending").length,
    };
  }, [orders]);

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-russo text-3xl md:text-4xl text-white uppercase tracking-tighter flex items-center gap-3">
            <Package className="w-8 h-8 text-demolition-red" />
            Orders
          </h1>
          <p className="font-mono text-sm text-white/50 mt-1">
            Merchandise orders from the Shop. Paid orders are ready to fulfill.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
          <button
            type="button"
            onClick={handleCleanupStale}
            disabled={cleaning}
            className="inline-flex items-center gap-2 px-4 py-2 border border-yellow-500/40
              text-yellow-300 hover:border-yellow-400 hover:bg-yellow-500/10 font-teko uppercase tracking-widest
              text-base transition-colors disabled:opacity-50"
            data-testid="admin-orders-cleanup-stale"
            title="Ask Stripe about every pending order older than 1 hour and flip expired ones"
          >
            <Sparkles className={`w-4 h-4 ${cleaning ? "animate-pulse" : ""}`} />
            {cleaning ? "Cleaning..." : "Clean Up Stale"}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/15
              text-white/80 hover:border-demolition-red hover:text-demolition-red font-teko uppercase tracking-widest
              text-base transition-colors disabled:opacity-50"
            data-testid="admin-orders-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-orders-stats">
        <StatCard label="Total Orders" value={stats.total} icon={Receipt} />
        <StatCard label="Paid" value={stats.paidCount} icon={ShoppingBag} accent />
        <StatCard label="Paid Revenue" value={fmtMoney(stats.paidRevenue)} icon={DollarSign} accent />
        <StatCard label="Pending" value={stats.pendingCount} icon={Truck} />
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="admin-orders-status-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value || "all"}
            type="button"
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 font-teko text-base uppercase tracking-widest border transition-colors ${
              statusFilter === tab.value
                ? "bg-demolition-red border-demolition-red text-white"
                : "border-white/15 text-white/70 hover:border-demolition-red hover:text-demolition-red"
            }`}
            data-testid={`admin-orders-filter-${tab.value || "all"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-demolition-paper border border-white/10 overflow-hidden" data-testid="admin-orders-table">
        {loading ? (
          <div className="p-12 text-center">
            <p className="font-mono text-white/50 animate-pulse">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-orders-empty">
            <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="font-mono text-white/60">No orders to show yet.</p>
            <p className="font-mono text-xs text-white/40 mt-1">
              Orders will appear here once fans complete a Stripe checkout.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-black/40 border-b border-white/10">
                <tr className="font-teko uppercase tracking-widest text-sm text-white/60">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3 hidden md:table-cell">Items</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const lineCount = (o.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
                  return (
                    <tr
                      key={o.id}
                      onClick={() => setSelected(o)}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`admin-order-row-${o.id}`}
                    >
                      <td className="px-4 py-4 font-mono text-xs text-white/70 align-top">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-white/30" />
                          {fmtDate(o.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <p className="font-mono text-sm text-white">{o.customer_name || "—"}</p>
                        <p className="font-mono text-xs text-white/50">{o.customer_email || "—"}</p>
                      </td>
                      <td className="px-4 py-4 align-top hidden md:table-cell font-mono text-sm text-white/80">
                        {lineCount} {lineCount === 1 ? "item" : "items"}
                      </td>
                      <td className="px-4 py-4 text-right font-russo text-base text-white">
                        {fmtMoney(o.amount_total)}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span
                          className={`inline-block px-2 py-0.5 font-teko text-sm uppercase tracking-widest ${statusBadgeClass(o.status)}`}
                          data-testid={`admin-order-status-${o.id}`}
                        >
                          {o.status || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div
      className={`border p-4 ${accent ? "border-demolition-red/40 bg-demolition-red/5" : "border-white/10 bg-demolition-paper"}`}
      data-testid={`admin-orders-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-teko uppercase tracking-widest text-xs text-white/50">{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-demolition-red" : "text-white/30"}`} />
      </div>
      <p className="font-russo text-2xl text-white">{value}</p>
    </div>
  );
}

function OrderDetailDrawer({ order, onClose, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const lines = order.line_items || [];
  const ship = order.shipping_address || null;
  const isPaid = order.status === "paid";
  return (
    <>
      <button
        type="button"
        aria-label="Close order details"
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
        data-testid="admin-order-drawer-backdrop"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Order details"
        className="fixed top-0 right-0 z-[61] h-full w-full sm:w-[520px] max-w-full
          bg-demolition-black border-l-2 border-demolition-red/60 shadow-2xl
          flex flex-col overflow-hidden"
        data-testid="admin-order-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-demolition-paper">
          <div>
            <h2 className="font-russo text-xl text-white uppercase tracking-tighter">
              Order Details
            </h2>
            <p className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1">
              <Hash className="w-3 h-3" />
              {order.id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-white/70 hover:text-demolition-red transition-colors"
            aria-label="Close"
            data-testid="admin-order-drawer-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <section>
            <SectionHeader>Status</SectionHeader>
            <div className="flex items-center gap-3">
              <span
                className={`inline-block px-3 py-1 font-teko text-base uppercase tracking-widest ${statusBadgeClass(order.status)}`}
              >
                {order.status || "—"}
              </span>
              <span className="font-mono text-xs text-white/50">
                Payment: {order.payment_status || "—"}
              </span>
            </div>
            <p className="font-mono text-xs text-white/50 mt-2">
              Created: {fmtDate(order.created_at)}
              {order.paid_at && <> · Paid: {fmtDate(order.paid_at)}</>}
            </p>
          </section>

          <section>
            <SectionHeader>Customer</SectionHeader>
            <div className="font-mono text-sm text-white space-y-1">
              <p>{order.customer_name || <span className="text-white/40">No name on file</span>}</p>
              {order.customer_email && (
                <p className="text-white/70 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  <a
                    href={`mailto:${order.customer_email}`}
                    className="hover:text-demolition-red transition-colors"
                  >
                    {order.customer_email}
                  </a>
                </p>
              )}
            </div>
          </section>

          {ship && (
            <section>
              <SectionHeader>Shipping Address</SectionHeader>
              <address className="font-mono text-sm text-white/80 not-italic flex gap-2">
                <MapPin className="w-4 h-4 text-demolition-red flex-shrink-0 mt-0.5" />
                <div>
                  {ship.name && <div>{ship.name}</div>}
                  {ship.line1 && <div>{ship.line1}</div>}
                  {ship.line2 && <div>{ship.line2}</div>}
                  <div>{[ship.city, ship.state, ship.postal_code].filter(Boolean).join(", ")}</div>
                  {ship.country && <div>{ship.country}</div>}
                </div>
              </address>
            </section>
          )}

          <section>
            <SectionHeader>Items</SectionHeader>
            {lines.length === 0 ? (
              <p className="font-mono text-sm text-white/40">No line items recorded.</p>
            ) : (
              <ul className="divide-y divide-white/5 border-y border-white/10">
                {lines.map((li, idx) => (
                  <li
                    key={`${li.item_id || idx}-${idx}`}
                    className="flex items-center justify-between py-3 font-mono text-sm"
                  >
                    <div>
                      <p className="text-white">{li.name}</p>
                      <p className="text-white/50 text-xs">
                        {fmtMoney((li.unit_amount || 0) / 100)} × {li.quantity}
                      </p>
                    </div>
                    <p className="text-white">
                      {fmtMoney(((li.unit_amount || 0) * li.quantity) / 100)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-1 font-mono text-sm">
            <div className="flex justify-between text-white/70">
              <span>Shipping</span>
              <span>{fmtMoney(order.shipping_total)}</span>
            </div>
            <div className="flex justify-between text-white/70">
              <span>Tax</span>
              <span>{fmtMoney(order.tax_total)}</span>
            </div>
            <div className="flex justify-between items-baseline border-t border-white/10 pt-2 mt-2">
              <span className="font-russo text-white uppercase tracking-tight text-base">Total</span>
              <span className="font-russo text-demolition-red text-2xl">
                {fmtMoney(order.amount_total)}
              </span>
            </div>
          </section>

          <section className="pt-2 border-t border-white/10">
            <SectionHeader>Stripe Session</SectionHeader>
            <p className="font-mono text-xs text-white/50 break-all" data-testid="admin-order-stripe-session">
              {order.session_id || "—"}
            </p>
          </section>

          <section className="pt-4 border-t border-red-900/40">
            <SectionHeader>Danger Zone</SectionHeader>
            <p className="font-mono text-xs text-white/50 mb-3">
              Permanently remove this order from the dashboard. This does <span className="text-white">not</span> touch
              Stripe or refund the customer — use it only to clean up test rows or after issuing a manual refund.
              {isPaid && (
                <span className="block mt-1 text-yellow-300">
                  Heads-up: this order is marked <strong>paid</strong>.
                </span>
              )}
            </p>
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/40
                  text-red-300 hover:border-red-400 hover:bg-red-500/10 font-teko uppercase tracking-widest
                  text-base transition-colors"
                data-testid="admin-order-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete Order
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onDelete(order)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500
                    text-white font-teko uppercase tracking-widest text-base transition-colors"
                  data-testid="admin-order-delete-confirm"
                >
                  <Trash2 className="w-4 h-4" />
                  Yes, delete permanently
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20
                    text-white/70 hover:text-white font-teko uppercase tracking-widest text-base transition-colors"
                  data-testid="admin-order-delete-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

function SectionHeader({ children }) {
  return (
    <h3 className="font-russo text-sm text-demolition-chrome uppercase tracking-widest mb-2">
      {children}
    </h3>
  );
}
